import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';

const STORAGE_KEY = 'cookie-consent';

const CookieConsent = () => {
  const { t, lang, dir } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleChoice = (choice: 'accepted' | 'declined') => {
    localStorage.setItem(STORAGE_KEY, choice);
    setVisible(false);
  };

  const isRTL = dir === 'rtl';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
          dir={isRTL ? 'rtl' : 'ltr'}
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="relative bg-card/95 backdrop-blur-md border border-primary/20 rounded-2xl shadow-2xl p-5 md:p-6">
              <button
                onClick={() => handleChoice('declined')}
                className="absolute top-3 end-3 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Cookie className="h-6 w-6 text-primary" />
                </div>

                <div className="flex-1 min-w-0">
                  <h2 id="cookie-consent-title" className="text-lg font-semibold text-foreground mb-1">
                    {t('cookies.title')}
                  </h2>
                  <p id="cookie-consent-desc" className="text-sm text-muted-foreground leading-relaxed">
                    {t('cookies.message')}{' '}
                    <Link
                      to={`/${lang}/privacy`}
                      className="text-primary hover:underline font-medium"
                    >
                      {t('cookies.learn_more')}
                    </Link>
                  </p>
                </div>

                <div className="flex flex-row gap-2 w-full md:w-auto flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChoice('declined')}
                    className="flex-1 md:flex-initial"
                  >
                    {t('cookies.decline')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleChoice('accepted')}
                    className="flex-1 md:flex-initial"
                  >
                    {t('cookies.accept')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
